import * as React from 'react' 
import clsx from 'clsx'
import { unstable_composeClasses as composeClasses } from '@mui/base'
import { refType, unstable_useId as useId } from '@mui/utils'
import { styled } from '@mui/material/styles'
import {
  Input,
  FilledInput,
  OutlinedInput,
  getTextFieldUtilityClass,
  FormControl,
  useThemeProps,
  InputLabel,
  Select,
  FormHelperText,
  TextField,
  TextFieldProps,
} from '@mui/material'
import TouchRipple from '@mui/material/ButtonBase/TouchRipple'
// import { useFormikContext } from 'formik'

const variantComponent = {
  standard: Input,
  filled: FilledInput,
  outlined: OutlinedInput,
}
const variantSpanSpacing = {
  standard: '.6rem',
  filled: '1.5rem',
  outlined: '1.5rem',
  standard_select: '1.1rem',
  filled_select: '1.5rem',
  outlined_select: '1.5rem',
}

const useUtilityClasses = (ownerState: any) => {
  const { classes } = ownerState

  const slots = {
    root: ['root'],
  }

  return composeClasses(slots, getTextFieldUtilityClass, classes)
}

const TextFieldRoot = styled(
  FormControl as unknown as keyof JSX.IntrinsicElements,
  {
    name: 'MuiTextField',
    slot: 'Root',
    overridesResolver: (props, styles) => styles['root'],
  }
)({})

export type AutoWidthTextFieldProps = TextFieldProps

function tryBigInt(value: any) {
  try {
    return BigInt(value as number)
  } catch (error) {
    return value
  }
}

export const AutoWidthTextField = React.forwardRef(function AutoWidthTextField(
  inProps: AutoWidthTextFieldProps,
  ref
): JSX.Element {
  // const { fieldKey } = inProps
  const props = useThemeProps({ props: inProps, name: 'MuiTextField' })
  const {
    autoComplete,
    autoFocus = false,
    children,
    className,
    color = 'primary',
    defaultValue,
    disabled = false,
    error = false,
    FormHelperTextProps,
    fullWidth = false,
    helperText,
    id: idOverride,
    InputLabelProps,
    inputProps,
    InputProps,
    inputRef,
    label,
    maxRows,
    minRows,
    multiline = false,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    required = false,
    rows,
    select = false,
    SelectProps,
    type,
    value,
    variant = 'outlined',
    ...other
  } = props
  const ownerState = {
    ...props,
    autoFocus,
    color,
    disabled,
    error,
    fullWidth,
    multiline,
    required,
    select,
    variant,
  }

  const classes = useUtilityClasses(ownerState)
  if (process.env['NODE_ENV'] !== 'production') {
    if (select && !children) {
      console.error(
        'MUI: `children` must be passed when using the `TextField` component with `select`.'
      )
    }
  }
  const InputMore: any = {}
  if (variant === 'outlined') {
    if (InputLabelProps && typeof InputLabelProps.shrink !== 'undefined') {
      InputMore.notched = InputLabelProps.shrink
    }
    InputMore.label = label
  }
  if (select) {
    // unset defaults from textbox inputs
    if (!SelectProps || !SelectProps.native) {
      InputMore.id = undefined
    }
    InputMore['aria-describedby'] = undefined
  }

  const id = useId(idOverride)
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined
  const inputLabelId = label && id ? `${id}-label` : undefined
  const InputComponent = variantComponent[variant]
  const InputElement = (
    <InputComponent
      aria-describedby={helperTextId}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      defaultValue={defaultValue}
      fullWidth={fullWidth}
      multiline={multiline}
      name={name}
      rows={rows}
      maxRows={maxRows}
      minRows={minRows}
      type={type}
      value={value}
      id={id}
      inputRef={inputRef}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      placeholder={placeholder}
      inputProps={{
        style: { /** padding: '0' ,*/ ...(inputProps?.style || {}) },
        ...inputProps,
      }}
      style={{ position: 'absolute', width: '100%', left: 0 }}
      {...InputMore}
      {...InputProps}
    />
  )
  return (
    // Don't know what the typescript deal is here yet...
    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    /* @ts-ignore */
    <FormControl
      className={clsx(classes.root, className)}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      ref={ref}
      required={required}
      color={color}
      variant={variant}
      ownerState={ownerState}
      style={{ minWidth: 'fit-content', ...(other?.style || {}) }}
      {...other}
    >
      {label != null && label !== '' && (
        <InputLabel htmlFor={id} id={inputLabelId} {...InputLabelProps}>
          {label}
        </InputLabel>
      )}

      <span
        style={{
          position: 'relative',
          // backgroundColor: 'blue',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            userSelect: `none`,
            color: 'transparent',
            // color: 'rgba(0, 0, 0, 0.22)',
            padding: `0 ${
              variantSpanSpacing[`${variant}${select ? `_select` : ``}`]
            }`,
            // backgroundColor: 'red',
          }}
        >
          {`${(value && tryBigInt(value)) || '0'}`}
        </span>
        {select ? (
          <Select
            aria-describedby={helperTextId}
            id={id}
            labelId={inputLabelId}
            value={`${(value && tryBigInt(value)) || '0'}`}
            input={InputElement}
            // style={{ width: `90px` }}
            style={{ position: 'absolute', width: '100%', left: 0 }}
            {...SelectProps}
          >
            {children}
          </Select>
        ) : (
          InputElement
        )}
      </span>

      {helperText && (
        <FormHelperText id={helperTextId} {...FormHelperTextProps}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  )
})
